import React, { useState, useEffect } from 'react';
import { Modal, Upload, Select, Button, message, Progress, Form, Table } from 'antd';
import uploadIcon from 'assets/images/image_doc.svg';
import uploadedIcon from 'assets/images/uploaded_icon.svg';
import templateIcon from 'assets/images/icon_template.svg';
import errorIcon from 'assets/images/icon_action_error.svg';
import { providedByOptions } from 'constants/knowledge-base';
import ConfirmModal from '../../../../components/confirm-modal';
import * as api from 'api';

import './upload-modal.css';

const { Option } = Select;
const { Item: FormItem } = Form;

const UploadModal = ({ visible, onCancel, selectedAppId, onUploadSuccess, isDocumentUpload }) => {
	const [fileList, setFileList] = useState([]);
	const [providedBy, setProvidedBy] = useState(undefined);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [isUploading, setIsUploading] = useState(false);
	const [hasUnsupportedFiles, setHasUnsupportedFiles] = useState(false);
	const [zeroFile, setzeroFile] = useState(false);
	const [uploadFailed, setUploadFailed] = useState(false);
	const [uploadSuccess, setUploadSuccess] = useState(false);
	const [hasUploaded, setHasUploaded] = useState(false);
	const [disabled, setDisabled] = useState(false);
	const [uploadSuccessCount, setUploadSuccessCount] = useState({
		totalFiles: 0,
		successFiles: 0,
	});
	const [form] = Form.useForm();
	const [confirmVisible, setConfirmVisible] = useState(false);
	const [errorRow, setErrorRow] = useState([])

	const resetAllStates = () => {
		setFileList([]);
		setUploadProgress(0);
		setIsUploading(false);
		setHasUnsupportedFiles(false);
		setzeroFile(false);
		setUploadFailed(false);
		setUploadSuccess(false);
		setUploadSuccessCount({
			totalFiles: 0,
			successFiles: 0,
		});
		setHasUploaded(false);
		form.resetFields();
	};

	useEffect(() => {
		if (fileList.length > 0) {
			setUploadSuccess(false);
			setUploadFailed(false);
			setDisabled(false);
		}
	}, [fileList]);

	const handleCancel = () => {
		const shouldRefresh = uploadSuccess && !isUploading;
		resetAllStates();
		onCancel(shouldRefresh);
	};

	const formatFileSize = (bytes) => {
		if (bytes === 0) return '0 Bytes';
		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
	};

	const getTotalFileSize = (files) => {
		return files.reduce((total, file) => total + (file.size || 0), 0);
	};

	const allowedFileTypes = isDocumentUpload ? ['.xlsx', '.xls'] : ['.html', '.htm', '.pdf', '.csv', '.xlsx', '.xls', '.txt', '.md'];

	useState(() => {
		setUploadSuccess(false);
		setUploadFailed(false);
	}, [fileList]);

	const handleUploadChange = ({ fileList: newFileList }) => {
		const unsupportedFiles = [];
		const filteredList = newFileList.filter((file) => {
			setzeroFile(false);
			setUploadFailed(false);
			const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
			const isAllowed = allowedFileTypes.includes(fileExtension);
			if (!isAllowed && file.status !== 'error') {
				unsupportedFiles.push(file.name);
				file.status = 'error';
			}
			return isAllowed;
		});
		setHasUnsupportedFiles(unsupportedFiles.length > 0);
		unsupportedFiles.forEach((fileName) => {
			message.error(`不支持的文件类型: ${fileName}`);
		});

		setFileList(filteredList);
	};

	const rest = () => {
		form.resetFields();
		setFileList([]);
		setUploadProgress(0);
		setIsUploading(false);
		setHasUnsupportedFiles(false);
		setzeroFile(false);
		setUploadFailed(false);
		setUploadSuccess(false);
		setUploadSuccessCount({
			totalFiles: 0,
			successFiles: 0,
		});
		setHasUploaded(false);
	}

	const onSubmit = async () => {
		try {
			await form.validateFields();
			if (fileList.length === 0) {
				setzeroFile(true);
				return;
			}
			setIsUploading(true);
			const handleProgress = (progressEvent) => {
				if (progressEvent.total) {
					const percent = Math.min(Math.round((progressEvent.loaded * 100) / progressEvent.total), 99);
					setUploadProgress(percent);
				}
			};
			if (isDocumentUpload) {
				const formData = new FormData();
				formData.append('file', fileList[0].originFileObj);
				providedBy?.forEach((sbu) => {
					formData.append('sbu', sbu);
				});
				const res = await api.uploadDocument(formData, selectedAppId);
				if (res.responseData.status === 'success') {
					setUploadSuccess(true);
					setUploadSuccessCount({
						totalFiles: 1,
						successFiles: 1,
					});
					setUploadProgress(100);
					setHasUploaded(true);
					setDisabled(true);
					onUploadSuccess();
					setFileList([]);
					rest();
					onCancel();
				} else {
					setUploadFailed(true);
					setFileList([]);
					setUploadProgress(0);
					rest();
					onCancel();
					setErrorRow(res.responseData.data);
					setConfirmVisible(true);
				}
			} else {
				const res = await api.uploadFiles(selectedAppId, fileList, providedBy, handleProgress);
				if (res.responseData.errors.length > 0) {
					setUploadFailed(true);
					setFileList([]);
					setUploadProgress(0);
				} else {
					setUploadSuccess(true);
					setUploadSuccessCount({
						totalFiles: res.responseData.totalFiles,
						successFiles: res.responseData.successCount,
					});
					setUploadProgress(100);
					setHasUploaded(true);
					setDisabled(true);
				}
				setFileList([]);
			}
		} catch (error) {
			if (!error.response) {
				return;
			}
			console.error('upload failed:', error);
			setUploadFailed(true);
			setFileList([]);
			setUploadProgress(0);
		} finally {
			setIsUploading(false);
		}
	};

	const handleProvidedByChange = (value) => {
		setProvidedBy(value);
	};

	const downloadTemplate = async (event) => {
		try {
			const res = await api.downloadDocumentTemplate();
			const url = window.URL.createObjectURL(new Blob([res]));
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', 'AI Chatbot  technical documents template without data.xlsx');
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error('download template failed:', error);
		}
	}

	const canSubmit = fileList.length > 0 && providedBy && providedBy.length > 0;

	const getSelectStyle = () => {
		return {
			width: 394,
			marginLeft: 8,
		};
	};

	const uploadProps = {
		name: 'file',
		multiple: true,
		accept: allowedFileTypes.join(','),
		fileList,
		onChange: handleUploadChange,
		beforeUpload: () => false,
		maxCount: 100,
		showUploadList: false,
	};
	return (
		<>
			<Modal
				title="Upload New"
				open={visible}
				onCancel={handleCancel}
				footer={null}
				width={960}
				className="upload-modal-text"
				destroyOnHidden={true}
				afterOpenChange={(open) => {
					if (open) {
						resetAllStates();
					}
				}}
			>
				<Form form={form} layout="vertical">
					<div className="modal-container">
						<div className="application-selector">
							<span className="application-selector-text">Application :</span>
							<span className="application-selector-name">AI Chatbot</span>
							<div className="application-selector-provider">
								<FormItem
									name="providedBy"
									rules={[
										{
											required: true,
											message: 'Provided by cannot be null',
										},
									]}
								>
									<Select
										value={providedBy}
										onChange={handleProvidedByChange}
										style={getSelectStyle()}
										placeholder="Provided by"
										allowClear
										mode="multiple"
									>
										{providedByOptions.map((option) => (
											<Option key={option.value} value={option.value}>
												{option.label}
											</Option>
										))}
									</Select>
								</FormItem>
							</div>
						</div>

						<Upload {...uploadProps}>
							<div className="upload-area-container" style={isDocumentUpload ? { height: '320px' } : { height: '280px' }}>
								<div className="upload-content">
									<img src={uploadIcon} alt="Upload" className="upload-icon" />
									<div className="upload-text">
										<p className="upload-text-p">
											{hasUploaded ? 'Continue to drop you file here or' : `Drop your file here or`}
											<a href="#" className="select-file-link">
												select file
											</a>
										</p>
										<p className={`support-text ${hasUnsupportedFiles ? 'unsupported-text' : ''}`}>
											{isDocumentUpload ? 'Support: xlsx, xls.' : 'Support: html, htm, pdf, csv, xlsx, xls, txt, md. Batch upload limited to 100 files.'}
										</p>
										{isDocumentUpload && <p className="support-text">Not sure about the setups for your technical files? Download a template below to start with.</p>}
										{isDocumentUpload && <div onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											downloadTemplate(e);
										}} className="download-template-link">
											<img src={templateIcon} alt="Template" className="template-icon" />
											AI Chatbot  technical documents template without data.xlsx
										</div>}
									</div>
									{zeroFile && <span className="error-text">Please select at least one file</span>}
									{fileList.length > 0 && !isUploading && (
										<div className="success-text">
											<img src={uploadedIcon} alt="Uploaded" className="uploaded-icon" />
											<span>
												{fileList.length} file{fileList.length > 1 ? 's' : ''} have been selected. Total size:{' '}
												{formatFileSize(getTotalFileSize(fileList))}
											</span>
										</div>
									)}
									{(isUploading || uploadFailed || uploadSuccess) && (
										<div className="uploading-progress">
											<div>
												<span>{`${uploadSuccessCount.successFiles}/${uploadSuccessCount.totalFiles}`}</span>
												<Progress
													showInfo={true}
													strokeColor={uploadFailed ? '#FF4D4F' : '#5BC78D'}
													percent={uploadProgress}
												/>
												{uploadSuccess && (
													<div className="success-text">
														<img src={uploadedIcon} alt="Uploaded" className="uploaded-icon" />
														<span>{uploadSuccessCount.successFiles} files have been submitted successfully !</span>
													</div>
												)}
												{isUploading && <div className="success-text">Submitting...</div>}
												{uploadFailed && <div className="error-text">Upload failed, please select files again.</div>}
											</div>
										</div>
									)}
								</div>
							</div>
						</Upload>
						<div className="modal-footer-text">
							<Button
								className="cancel-btn"
								key="cancel"
								variant={isUploading ? 'filled' : 'solid'}
								onClick={handleCancel}
								disabled={isUploading}
							>
								Cancel
							</Button>
							<Button
								className={isUploading || disabled || !canSubmit ? 'add-btn-disable' : 'add-btn'}
								color="purple"
								variant="solid"
								onClick={onSubmit}
								disabled={isUploading || disabled || !canSubmit}
							>
								{isDocumentUpload ? 'Save' : 'Submit for Approval'}
							</Button>
						</div>
					</div>
				</Form>
				
			</Modal>
			<ConfirmModal
					visible={confirmVisible}
					onOk={() => setConfirmVisible(false)}
					onCancel={() => setConfirmVisible(false)}
					showIcon={false}
					showFooter={false}
					width={960}
					title={
						<div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}><img src={errorIcon} alt="Template" className="error-icon" />Oops, upload failed due to data errors as below:</div>
					}
					content={
						<>
							<div className="modal-section-sub-text">All the details could be checked in download document.</div>
							<Table
								rowKey="rowNum"
								size="small"
								className="modelTable"
								dataSource={errorRow}
								columns={[
									{
										title: 'Row Number',
										dataIndex: 'rowNum',
										key: 'rowNum',
										width: 150,
									},
									 {
										title: 'Error in Data',
										dataIndex: 'errorMessage',
										key: 'errorMessage',
										width: 690,
										render: (text) => {
											return <div style={{ whiteSpace: 'pre-wrap', lineHeight: '24px' }}>{text}</div>;
										},
									},
								]}
								pagination={false}
								scroll={{ x: 840, y: 355 }}
							/>
						</>
					}
				></ConfirmModal>
		</>
	);
};

export default UploadModal;
